import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { listProducts } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import ProductCard from "../components/ProductCard";
import Filters from "../components/Filters";
import { Loader2 } from "lucide-react";

const PAGE_SIZE = 24;

export default function Category() {
  const { slug } = useParams();
  const category = useMemo(
    () => CATEGORIES.find((c) => c.slug === slug),
    [slug]
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ min_price: undefined, max_price: undefined });
  const [attrFilters, setAttrFilters] = useState({});
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setPage(0);
    setFilters({ min_price: undefined, max_price: undefined });
    setAttrFilters({});
    setSort("featured");
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // Fetch a larger batch to enable client-side attribute filtering + pagination display.
    listProducts({
      category: slug,
      sort,
      skip: 0,
      limit: 120,
      ...(filters.min_price !== undefined ? { min_price: filters.min_price } : {}),
      ...(filters.max_price !== undefined ? { max_price: filters.max_price } : {}),
    })
      .then((data) => {
        if (!mounted) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug, sort, filters]);

  // Derive attribute options from currently-loaded items
  const attrOptions = useMemo(() => {
    const opts = {};
    for (const p of items) {
      const a = p.attributes || {};
      for (const [k, v] of Object.entries(a)) {
        if (v === null || v === undefined || Array.isArray(v)) continue;
        const s = String(v);
        opts[k] = opts[k] || new Set();
        opts[k].add(s);
      }
    }
    // Convert Sets → sorted arrays
    const out = {};
    for (const [k, set] of Object.entries(opts)) {
      const arr = Array.from(set);
      // Sort numerically when possible
      arr.sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return String(a).localeCompare(String(b));
      });
      out[k] = arr;
    }
    return out;
  }, [items]);

  // Apply client-side attribute filters
  const filtered = useMemo(() => {
    const active = Object.entries(attrFilters).filter(([, v]) => v);
    if (active.length === 0) return items;
    return items.filter((p) => {
      const a = p.attributes || {};
      return active.every(([k, v]) => String(a[k]) === String(v));
    });
  }, [items, attrFilters]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  if (!category) {
    return (
      <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Category not found</h1>
        <Link to="/" className="text-sm mt-4 inline-block underline">
          Back to home
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-8" data-testid="category-page">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-xs text-neutral-500 mb-3" data-testid="breadcrumb">
          <Link to="/" className="hover:text-neutral-900">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-900 font-medium">{category.label}</span>
        </nav>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter text-neutral-900">
          {category.label}
        </h1>
        <p className="text-neutral-600 mt-2 text-sm">{category.tagline}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <Filters
          category={slug}
          filters={filters}
          setFilters={setFilters}
          sort={sort}
          setSort={setSort}
          attrOptions={attrOptions}
          attrFilters={attrFilters}
          setAttrFilters={setAttrFilters}
          onReset={() => {
            setFilters({ min_price: undefined, max_price: undefined });
            setAttrFilters({});
            setSort("featured");
            setPage(0);
          }}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-neutral-600" data-testid="results-count">
              Showing{" "}
              <span className="font-medium text-neutral-900">{filtered.length}</span>{" "}
              of <span className="font-medium text-neutral-900">{total}</span> results
            </p>
          </div>

          {loading ? (
            <div className="py-24 flex items-center justify-center text-neutral-500">
              <Loader2 className="animate-spin mr-2" size={18} />
              Loading products…
            </div>
          ) : paged.length === 0 ? (
            <div className="py-24 text-center text-neutral-500 border border-dashed border-neutral-300 rounded-xl">
              No products match those filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid">
              {paged.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-9 px-4 rounded-full border border-neutral-300 text-sm disabled:opacity-40 hover:bg-neutral-100 transition-colors"
                data-testid="pagination-prev"
              >
                ← Prev
              </button>
              <span className="text-sm text-neutral-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="h-9 px-4 rounded-full border border-neutral-300 text-sm disabled:opacity-40 hover:bg-neutral-100 transition-colors"
                data-testid="pagination-next"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
